import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
// Manual spherical-Mercator projector (self-contained; no geo dependency).
const D2R = Math.PI / 180;
const mercY = (lat) => Math.log(Math.tan(Math.PI / 4 + (lat * D2R) / 2));

function makeProjection(features) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const walk = (c) => {
    if (typeof c[0] === "number") {
      const x = c[0] * D2R;
      const y = mercY(c[1]);
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    } else c.forEach(walk);
  };
  features.forEach((f) => walk(f.geometry.coordinates));

  const padX = 44, padTop = 30, padBottom = 30;
  const s = Math.min((W - 2 * padX) / (maxX - minX), (H - padTop - padBottom) / (maxY - minY));
  const tx = padX + (W - 2 * padX - s * (maxX - minX)) / 2 - s * minX;
  const ty = padTop + (H - padTop - padBottom - s * (maxY - minY)) / 2 + s * maxY;
  return (lonLat) => [tx + s * lonLat[0] * D2R, ty - s * mercY(lonLat[1])];
}

function featurePath(geometry, project) {
  const poly = (rings) =>
    rings
      .map((ring) => ring.map((c, i) => {
        const [x, y] = project(c);
        return `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`;
      }).join("") + "Z")
      .join("");
  return geometry.type === "Polygon"
    ? poly(geometry.coordinates)
    : geometry.coordinates.map(poly).join("");
}
import { NETWORK } from "../data/site";

const W = 720;
const H = 800;
// Roughly follows the national highway arc: Bengaluru → Hyderabad → Nagpur →
// Jhansi → Delhi NCR (west spur to Jaipur/Ahmedabad; Mumbai–Pune branch),
// eastern arc to Kolkata → Guwahati, southern arc to Chennai → Kochi.
const ARC = 72;

const HQ = NETWORK.hq;
const HQ_LL = [HQ.lon, HQ.lat];
const HQ_LOC = { ...HQ, id: HQ.id, status: "hq" };
const ALL = [HQ_LOC, ...NETWORK.locations];

function arcPath(p1, p2) {
  const dx = p2[0] - p1[0];
  const dy = p2[1] - p1[1];
  const mx = (p1[0] + p2[0]) / 2;
  const my = (p1[1] + p2[1]) / 2;
  const len = Math.hypot(dx, dy) || 1;
  const cx = mx + (-dy / len) * ARC;
  const cy = my + (dx / len) * ARC;
  return `M ${p1[0]} ${p1[1]} Q ${cx} ${cy} ${p2[0]} ${p2[1]}`;
}

// Label nudges (SVG px) so names never collide or cross the map edge.
const NUDGE = {
  delhi: { dx: 12, dy: -4, anchor: "start" },
  jaipur: { dx: -12, dy: -4, anchor: "end" },
  lucknow: { dx: 10, dy: -6, anchor: "start" },
  ahmedabad: { dx: -12, dy: 0, anchor: "end" },
  mumbai: { dx: -12, dy: 4, anchor: "end" },
  pune: { dx: -4, dy: 18, anchor: "middle" },
  hyderabad: { dx: 14, dy: 6, anchor: "start" },
  chennai: { dx: 12, dy: 10, anchor: "start" },
  kochi: { dx: 2, dy: 18, anchor: "middle" },
  kolkata: { dx: 12, dy: -6, anchor: "start" },
  guwahati: { dx: 10, dy: -6, anchor: "start" },
  bengaluru: { dx: 12, dy: -12, anchor: "start" },
};

export default function NetworkMap() {
  const [geo, setGeo] = useState(null);
  const [active, setActive] = useState(HQ_LOC);

  useEffect(() => {
    let on = true;
    fetch("/india-states.geojson")
      .then((r) => r.json())
      .then((d) => on && setGeo(d))
      .catch(() => {});
    return () => { on = false; };
  }, []);

  const { statePaths, xy } = useMemo(() => {
    if (!geo) return { statePaths: [], xy: {} };
    const project = makeProjection(geo.features);
    const paths = geo.features.map((f) => featurePath(f.geometry, project)).filter(Boolean);
    const map = {};
    for (const loc of ALL) map[loc.id] = project([loc.lon, loc.lat]);
    return { statePaths: paths, xy: map };
  }, [geo]);

  const activePt = xy[active.id];
  const hqPt = xy[HQ.id];
  const isPlanned = active.status === "planned";

  return (
    <div
      className="relative border border-gold/20 bg-surface p-4 sm:p-8"
      data-testid="network-map"
      data-state={geo ? "loaded" : "loading"}
    >
      {!geo && (
        <div className="flex aspect-[9/10] w-full items-center justify-center" data-testid="network-map-loading">
          <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-gold/60">
            Charting the national network…
          </p>
        </div>
      )}

      {geo && (
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="mx-auto w-full max-w-2xl"
          role="img"
          aria-label="Accurate map of India showing The Royal Mahaveer Roadlines logistics network from Bengaluru headquarters to Delhi NCR, Jaipur, Lucknow, Ahmedabad, Mumbai, Pune, Hyderabad, Chennai, Kochi, Kolkata and Guwahati"
          data-testid="india-map-svg"
        >
          <defs>
            <linearGradient id="nmGold" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#E5C65A" />
              <stop offset="1" stopColor="#C9A227" />
            </linearGradient>
          </defs>

          {/* Accurate state & UT boundaries — non-interactive so node clicks pass through */}
          <g style={{ pointerEvents: "none" }}>
            {statePaths.map((d, i) => (
              <path
                key={i}
                d={d}
                fill="rgba(201,162,39,0.04)"
                stroke="#C9A227"
                strokeOpacity="0.38"
                strokeWidth="1"
                strokeLinejoin="round"
              />
            ))}
          </g>

          {/* Route corridors radiating from Bengaluru — non-interactive */}
          <g style={{ pointerEvents: "none" }}>
          {hqPt &&
            NETWORK.locations.map((loc, i) => {
              const pt = xy[loc.id];
              if (!pt) return null;
              const d = arcPath(hqPt, pt);
              const selected = active.id === loc.id;
              const planned = loc.status === "planned";
              return (
                <motion.path
                  key={`route-${loc.id}`}
                  d={d}
                  fill="none"
                  stroke={selected ? "#E5C65A" : planned ? "#D9A441" : "#C9A227"}
                  strokeWidth={selected ? 2.2 : planned ? 1 : 1.3}
                  strokeDasharray={planned ? "2 7" : "1 7"}
                  strokeLinecap="round"
                  strokeOpacity={selected ? 0.95 : planned ? 0.4 : 0.55}
                  initial={{ pathLength: 0 }}
                  whileInView={{ pathLength: 1 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 1.4, delay: 0.18 * i, ease: "easeInOut" }}
                />
              );
            })}
          </g>

          {/* HQ pulse rings — non-interactive */}
          {hqPt && (
            <g style={{ pointerEvents: "none" }}>
              <motion.circle
                cx={hqPt[0]} cy={hqPt[1]} r="7"
                fill="none" stroke="#E5C65A" strokeWidth="1.2"
                initial={{ opacity: 0.9, scale: 0.4 }}
                animate={{ opacity: 0, scale: 3.2 }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut" }}
                style={{ transformOrigin: `${hqPt[0]}px ${hqPt[1]}px` }}
              />
              <motion.circle
                cx={hqPt[0]} cy={hqPt[1]} r="7"
                fill="none" stroke="#C9A227" strokeWidth="0.8"
                initial={{ opacity: 0.7, scale: 0.4 }}
                animate={{ opacity: 0, scale: 3.2 }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut", delay: 1.2 }}
                style={{ transformOrigin: `${hqPt[0]}px ${hqPt[1]}px` }}
              />
            </g>
          )}

          {/* Location markers + labels (interactive) */}
          <g>
            {ALL.map((loc, i) => {
              const pt = xy[loc.id];
              if (!pt) return null;
              const n = NUDGE[loc.id] || { dx: 12, dy: 0, anchor: "start" };
              const isHq = loc.id === HQ.id;
              const planned = loc.status === "planned";
              const selected = active.id === loc.id;
              return (
                <motion.g
                  key={loc.id}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: isHq ? 0.1 : 0.18 * i + 0.6 }}
                  data-testid={`map-node-${loc.id}`}
                  role="button"
                  tabIndex={0}
                  aria-label={`${loc.label}, ${loc.state}${isHq ? " — corporate headquarters" : planned ? " — planned corridor" : " — operational corridor"}`}
                  className="cursor-pointer outline-none"
                  onClick={() => setActive(loc)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setActive(loc);
                    }
                  }}
                >
                  <circle cx={pt[0]} cy={pt[1]} r={isHq ? 20 : 15} fill="rgba(0,0,0,0.001)" />
                  {planned && (
                    <circle cx={pt[0]} cy={pt[1]} r={selected ? 6.5 : 5} fill="none" stroke="#D9A441" strokeWidth="0.8" strokeOpacity="0.7" strokeDasharray="1.5 2.5" />
                  )}
                  <circle
                    cx={pt[0]} cy={pt[1]}
                    r={isHq ? 6 : selected ? 5 : 3.4}
                    fill={isHq || selected ? "#E5C65A" : planned ? "#D9A441" : "#C9A227"}
                    fillOpacity={planned && !selected ? 0.75 : 1}
                    stroke={isHq || selected ? "#F5F5F5" : "none"}
                    strokeWidth={isHq ? 1.4 : selected ? 1 : 0}
                  />
                  <text
                    x={pt[0] + n.dx}
                    y={pt[1] + n.dy + 3}
                    textAnchor={n.anchor}
                    fill={selected || isHq ? "#E5C65A" : "#F5F5F5"}
                    fillOpacity={selected || isHq ? 1 : planned ? 0.5 : 0.82}
                    fontSize={isHq ? 13 : selected ? 11.5 : 10.5}
                    fontWeight={isHq || selected ? 600 : 500}
                    fontFamily="Manrope, sans-serif"
                    letterSpacing="1"
                    style={{ pointerEvents: "none" }}
                  >
                    {loc.label.toUpperCase()}
                  </text>
                </motion.g>
              );
            })}
          </g>

          {/* HQ role caption — non-interactive */}
          {hqPt && (
            <text
              x={hqPt[0] + 12} y={hqPt[1] + 3}
              textAnchor="start" fill="#C9A227" fillOpacity="0.85"
              fontSize="8.5" fontFamily="Manrope, sans-serif" letterSpacing="1.6"
              style={{ pointerEvents: "none" }}
            >
              CORPORATE HQ
            </text>
          )}
        </svg>
      )}

      {/* Selected-location detail */}
      <AnimatePresence mode="wait">
        <motion.div
          key={active.id}
          data-testid="map-detail-panel"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.25 }}
          className="mt-6 border border-gold/20 bg-ink p-5"
        >
          <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
            <p className="font-heading text-lg text-foreground">{active.city}</p>
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              {active.state}
            </p>
          </div>
          <span
            data-testid="map-detail-badge"
            className={`mt-3 inline-block border px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.22em] ${
              active.id === HQ.id
                ? "border-gold/50 bg-gold/10 text-gold-light"
                : isPlanned
                  ? "border-amber-500/40 bg-amber-500/10 text-amber-400"
                  : "border-gold/30 bg-gold/5 text-gold"
            }`}
          >
            {active.id === HQ.id ? "Corporate Headquarters" : isPlanned ? "Planned Corridor — Coming Soon" : "Operational Corridor"}
          </span>
          <p data-testid="map-detail-coverage" className="mt-3 text-sm leading-relaxed text-foreground/85">
            {active.coverage}
          </p>
          {activePt && (
            <p className="mt-3 text-[9px] font-semibold uppercase tracking-[0.25em] text-muted-foreground/60">
              {active.lat.toFixed(2)}° N · {active.lon.toFixed(2)}° E
            </p>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Legend */}
      <div className="mt-5 flex flex-wrap items-center gap-x-7 gap-y-2 border-t border-gold/10 pt-5" data-testid="map-legend">
        <span className="flex items-center gap-2 text-[9px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
          <span className="inline-block h-2 w-2 rounded-full bg-gold-light" /> Headquarters
        </span>
        <span className="flex items-center gap-2 text-[9px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
          <span className="inline-block h-2 w-2 rounded-full bg-gold" /> Operational
        </span>
        <span className="flex items-center gap-2 text-[9px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
          <span className="inline-block h-2 w-2 rounded-full border border-amber-400/70" /> Planned
        </span>
        <span className="flex items-center gap-2 text-[9px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
          <svg width="26" height="4" aria-hidden="true"><line x1="0" y1="2" x2="26" y2="2" stroke="#C9A227" strokeWidth="1.4" strokeDasharray="2 5" strokeLinecap="round" /></svg>
          Service Corridor
        </span>
      </div>
    </div>
  );
}
